import React, { useState, useEffect, useMemo } from 'react';
import { db, functions } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import {
  collection, query, where, onSnapshot, doc, updateDoc, setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Mail, BookOpen, UserPlus, Search, Calendar, CheckCircle, XCircle, X
} from 'lucide-react';

export default function AuthorMailingListTab() {
  const { authorId } = useAuth();
  const { books, campaigns } = useAuthorDashboard();
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [converting, setConverting] = useState({});
  const [decliningId, setDecliningId] = useState(null); // signup.id currently showing decline form
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState({});

  // Real-time listener for mailingListSignups where authorId matches
  useEffect(() => {
    if (!authorId) return;
    const q = query(
      collection(db, 'mailingListSignups'),
      where('authorId', '==', authorId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSignups(data);
      setLoading(false);
    }, (err) => {
      console.error('Mailing list listener error:', err);
      setLoading(false);
    });
    return unsub;
  }, [authorId]);

  // Build a lookup map of book titles
  const bookTitles = useMemo(() => {
    const map = {};
    books.forEach(b => { map[b.id] = b.title || b.id; });
    return map;
  }, [books]);

  // Group signups by bookId
  const groupedByBook = useMemo(() => {
    const groups = {};
    const filtered = signups.filter(s => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (s.name || '').toLowerCase().includes(term)
        || (s.email || '').toLowerCase().includes(term)
        || (s.bookTitle || '').toLowerCase().includes(term);
    });
    filtered.forEach(s => {
      const key = s.bookId || 'unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    });
    // Sort entries within each group by signedUpAt descending
    Object.values(groups).forEach(arr =>
      arr.sort((a, b) => {
        const aTime = a.signedUpAt?.toDate?.() || new Date(0);
        const bTime = b.signedUpAt?.toDate?.() || new Date(0);
        return bTime - aTime;
      })
    );
    return groups;
  }, [signups, searchTerm]);

  const bookIds = Object.keys(groupedByBook);
  const totalSignups = signups.length;
  const activeSignups = signups.filter(s => s.status === 'mailingList').length;
  const convertedSignups = signups.filter(s => s.status === 'converted').length;

  // Approve to ARC: create campaign application + update signup status
  const handleApproveToArc = async (signup) => {
    if (converting[signup.id]) return;
    setConverting(prev => ({ ...prev, [signup.id]: true }));
    try {
      // Find the campaign for this book
      const campaign = campaigns.find(c => c.bookId === signup.bookId);
      if (!campaign) {
        alert('No active campaign found for this book. Create an ARC campaign first.');
        return;
      }
      // Create campaign application
      await setDoc(
        doc(db, 'authors', authorId, 'campaigns', campaign.id, 'applications', signup.userId),
        {
          readerId: signup.userId,
          readerName: signup.name || signup.email,
          readerEmail: signup.email,
          readerScore: 0,
          arcsCompleted: 0,
          genres: [],
          platforms: [],
          message: 'Converted from mailing list signup',
          status: 'approved',
          appliedAt: serverTimestamp(),
          bookId: signup.bookId,
          bookTitle: signup.bookTitle || '',
          authorId,
          source: 'mailing_list_conversion',
        }
      );
      // Update signup status to converted
      await updateDoc(doc(db, 'mailingListSignups', signup.id), {
        status: 'converted',
        convertedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Approve to ARC failed:', err);
      alert('Error: ' + err.message);
    } finally {
      setConverting(prev => ({ ...prev, [signup.id]: false }));
    }
  };

  // Decline a mailing list signup
  const handleDecline = async (signup) => {
    if (declining[signup.id]) return;
    setDeclining(prev => ({ ...prev, [signup.id]: true }));
    try {
      await updateDoc(doc(db, 'mailingListSignups', signup.id), {
        status: 'declined',
        declinedAt: serverTimestamp(),
        declineReason: declineReason.trim() || '',
      });
      setDecliningId(null);
      setDeclineReason('');
    } catch (err) {
      console.error('Decline failed:', err);
      alert('Error: ' + err.message);
    } finally {
      setDeclining(prev => ({ ...prev, [signup.id]: false }));
    }
  };

  const declinedSignups = signups.filter(s => s.status === 'declined').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
          <p className="text-sm text-slate-500">Loading mailing list...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Mailing List</h2>
          <p className="text-sm text-slate-500">Readers who signed up for book updates</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="flex items-center gap-3 px-4 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 text-teal-600">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">{totalSignups}</p>
              <p className="text-xs text-slate-500">Total signups</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="flex items-center gap-3 px-4 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">{activeSignups}</p>
              <p className="text-xs text-slate-500">Active subscribers</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="flex items-center gap-3 px-4 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">{convertedSignups}</p>
              <p className="text-xs text-slate-500">Converted to ARC</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="flex items-center gap-3 px-4 py-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
              <XCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-slate-900">{declinedSignups}</p>
              <p className="text-xs text-slate-500">Declined</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name, email, or book..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
      </div>

      {/* Grouped by book */}
      {bookIds.length === 0 ? (
        <Card className="rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="py-12 text-center">
            <Mail className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">No mailing list signups yet</p>
          </CardContent>
        </Card>
      ) : (
        bookIds.map(bookId => (
          <div key={bookId} className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              <BookOpen className="h-4 w-4 text-teal-600" />
              <h3 className="text-sm font-semibold text-slate-700">
                {bookTitles[bookId] || groupedByBook[bookId][0]?.bookTitle || bookId}
              </h3>
              <Badge className="bg-slate-100 text-slate-600 text-[10px] border-0">
                {groupedByBook[bookId].length}
              </Badge>
            </div>
            <Card className="rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="divide-y divide-slate-100">
                {groupedByBook[bookId].map(signup => (
                  <div key={signup.id} className={`px-5 py-3 ${signup.status === 'declined' ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`flex h-9 w-9 items-center justify-center rounded-full ${
                          signup.status === 'declined' ? 'bg-red-50 text-red-400' : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Mail className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{signup.name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{signup.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Calendar className="h-3 w-3" />
                          {signup.signedUpAt?.toDate?.()
                            ? signup.signedUpAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                            : '—'}
                        </div>
                        {signup.status === 'converted' ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px] border-0">
                            <CheckCircle className="mr-1 h-3 w-3" /> ARC Approved
                          </Badge>
                        ) : signup.status === 'declined' ? (
                          <Badge className="bg-red-100 text-red-600 text-[10px] border-0">
                            <XCircle className="mr-1 h-3 w-3" /> Declined
                          </Badge>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveToArc(signup)}
                              disabled={converting[signup.id]}
                              className="bg-gradient-to-r from-slate-500 to-teal-600 px-3 py-1 text-xs font-semibold text-white hover:from-teal-600 hover:to-teal-700"
                            >
                              {converting[signup.id] ? 'Converting...' : 'Approve to ARC'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setDecliningId(decliningId === signup.id ? null : signup.id); setDeclineReason(''); }}
                              className="border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 hover:border-red-300"
                            >
                              Decline
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Inline decline reason form */}
                    {decliningId === signup.id && signup.status === 'mailingList' && (
                      <div className="mt-3 ml-13 flex items-center gap-2 rounded-lg border border-red-100 bg-red-50/50 p-3">
                        <input
                          type="text"
                          placeholder="Reason (optional)"
                          value={declineReason}
                          onChange={(e) => setDeclineReason(e.target.value)}
                          className="flex-1 rounded border border-red-200 bg-white px-3 py-1.5 text-xs text-slate-900 placeholder-slate-400 focus:border-red-400 focus:outline-none"
                        />
                        <Button
                          size="sm"
                          onClick={() => handleDecline(signup)}
                          disabled={declining[signup.id]}
                          className="bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
                        >
                          {declining[signup.id] ? 'Declining...' : 'Confirm'}
                        </Button>
                        <button
                          onClick={() => { setDecliningId(null); setDeclineReason(''); }}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    {/* Show decline reason if declined */}
                    {signup.status === 'declined' && signup.declineReason && (
                      <p className="mt-1 ml-13 text-[11px] text-red-400 italic">Reason: {signup.declineReason}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        ))
      )}
    </div>
  );
}
